import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router";
import { z } from "zod";

import { useAuth } from "@/auth/authContext";
import { clearLoginNotice, peekLoginNotice } from "@/auth/loginNotice";
import { BrandMark } from "@/components/BrandMark";

// 帳號只檢查「有填」：它可能是 email 也可能是手機號碼，格式與正規化
// （0912-345-678、+886…）由後端判斷，前端再寫一份規則只會與後端不同步。
const schema = z.object({
  account: z.string().trim().min(1, "請輸入 email 或手機號碼"),
  password: z.string().min(1, "請輸入密碼"),
});

type FormValues = z.infer<typeof schema>;

/**
 * 標籤放在欄位**上方**而不是用 MUI 的浮動標籤。
 *
 * 中性設計語言裡，浮動標籤那個動畫與縮小的字是 Material 的招牌動作，
 * 換皮之後看起來會突兀。標籤在上方也讓「欄位是空的」與「欄位有值」
 * 兩種狀態長得一樣高，表單不會跳動。
 *
 * `htmlFor` / `id` 一定要成對 —— 少了它，點標籤不會聚焦，讀螢幕軟體也
 * 唸不出這個欄位是什麼。
 */
function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.875 }}>
      <Typography component="label" htmlFor={id} variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function LoginPage() {
  const { me, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { account: "", password: "" },
  });

  // 從別處被送回登入頁時的說明（例如改完密碼），讓使用者知道為什麼要重新登入。
  // 顯示一次就清掉，下次登出回來不會又看到舊的訊息。
  const [notice] = useState(peekLoginNotice);
  useEffect(() => clearLoginNotice(), []);

  if (me) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/"} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      await login(values.account, values.password);
      void navigate("/", { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "登入失敗。");
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        minHeight: "100dvh",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: `${shape.shell}px`,
          p: { xs: 3, sm: 4.5 },
        }}
      >
        {/*
          登入頁的標誌是這一頁**唯一**的識別資訊（沒有側欄、沒有其他文字
          說明這是哪個系統），所以要給替代文字
        */}
        <BrandMark size={42} alt="力泰建設" />

        <Typography variant="h4" component="h1" sx={{ mt: 3.25 }}>
          登入
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          請使用公司 email 或手機號碼登入。
        </Typography>

        <Box
          component="form"
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
          noValidate
          sx={{ mt: 3.75 }}
        >
          <Stack spacing={2.25}>
            {submitError ? (
              <Alert severity="error">{submitError}</Alert>
            ) : (
              notice && <Alert severity="success">{notice}</Alert>
            )}

            <Field id="login-account" label="Email 或手機號碼">
              <TextField
                {...register("account")}
                id="login-account"
                // 不用 type="email"：手機號碼過不了瀏覽器的 email 檢查，
                // 手機上也會跳出不方便打數字的鍵盤
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                fullWidth
                size="medium"
                error={Boolean(errors.account)}
                helperText={errors.account?.message}
              />
            </Field>

            <Field id="login-password" label="密碼">
              <TextField
                {...register("password")}
                id="login-password"
                type="password"
                autoComplete="current-password"
                fullWidth
                size="medium"
                error={Boolean(errors.password)}
                helperText={errors.password?.message}
              />
            </Field>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              sx={{ minHeight: 48, mt: 0.5 }}
            >
              {isSubmitting ? "登入中…" : "登入"}
            </Button>

            {/* 這個平台沒有自助重設密碼的流程，密碼一律由管理員在後台重設 */}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              忘記密碼請聯絡系統管理員重設。
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
